import { asOfDateSchema, forecastRunPurposeSchema } from "@erp/shared-types";
import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const runDemandForecastStep = defineStepConfig({
  type: "run_demand_forecast",
  name: "Run Demand Forecast",
  description:
    "Aggregates outbound demand history (sales-out + transfers-out) per SKU×warehouse, forecasts the next horizon of months (Holt-Winters / moving-average), derives safety stock + reorder point, and persists the forward projection (im_demand_forecasts) and policy outputs.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z
    .object({
      divisionId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "Scope the run to one division. Omit to forecast every division.",
        ),
      asOfDate: asOfDateSchema
        .optional()
        .describe("Frozen YYYY-MM-DD run date for deterministic replay."),
      purpose: forecastRunPurposeSchema
        .exclude(["production", "single_series"])
        .optional()
        .describe(
          "Required for a frozen replay; production runs use the current date.",
        ),
      dryRun: z
        .boolean()
        .optional()
        .describe(
          "If true, forecast but do not persist forecasts or policy outputs. Defaults to false.",
        ),
    })
    .superRefine((input, context) => {
      if (input.asOfDate && !input.purpose) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["purpose"],
          message:
            "Frozen asOfDate requires an explicit non-production purpose.",
        });
      }
    }),
  // Only the run id is exposed downstream — it's the handle to query this run's
  // rows in im_demand_forecasts. Per-run counts are logged, not surfaced.
  outputSchema: z.object({
    forecastRunId: z.string().uuid(),
  }),
  configSchema: z.object({
    historyMonths: z
      .number()
      .int()
      .min(12)
      .max(60)
      .prefault(24)
      .describe(
        "Months of outbound history to read. ≥24 lets Holt-Winters fit ≥2 seasonal cycles.",
      ),
    horizonMonths: z
      .number()
      .int()
      .min(1)
      .max(24)
      .prefault(6)
      .describe("Number of future months to project per series."),
    availabilityByPopularity: z
      .record(z.string().min(1).max(10), z.number().gt(0).lt(1))
      .optional()
      .describe(
        "Target availability (0..1, exclusive) per popularity code, e.g. " +
          '{"A": 0.99, "B": 0.97, "N": 0.5}. Codes left out — and items with ' +
          "no code — keep the engine's demand-class default, so an omitted " +
          "map is exactly the previous behavior. Codes are matched " +
          "case-insensitively.",
      ),
  }),
  emits: [] as const,
  execution: {
    // Per-series forecasting is CPU-bound across all divisions — give it room.
    startToCloseTimeout: "60 minutes",
    // Committed divisions are checkpointed under one stable run ID. Leave time
    // for a cancelled worker to release its execution lock before retrying.
    scheduleToCloseTimeout: "2 hours",
    retry: { maximumAttempts: 3, initialInterval: "1 minute" },
    // Every division refusing its inputs is deterministic: a retry would redo
    // the whole calculation against the same broken data and refuse again.
    nonRetryableErrorTypes: [
      "LeadTimeCoverageRefusal",
      "DemandKeyCoverageRefusal",
    ],
  },
});
